/**
 * Powerup Type Definitions
 * Modular powerup system for special ball drops
 */

import powerupConfigData from '@/config/powerupConfig.json';

/**
 * Powerup configuration interface
 */
export interface PowerupConfig {
  name: string;
  sprite: string;
  assetPath: string;
  duration: number; // Effect duration in milliseconds
  displayDuration: number; // How long to show the powerup name text
  effect: 'freeze_balls' | 'extra_time' | 'invincibility' | 'double_points' | 'apply_shield' | 'apply_multi_shot' | 'apply_rapid_fire' | 'apply_score_2x' | 'apply_extra_life'; // Extensible effect types
  dropSpeed: number; // How fast the powerup falls when dropped
  collectRadius: number; // Collection hitbox radius
  glowColor: string; // Hex color for glow effect (e.g., "0xFFFF00" for yellow)
  glowBlinkSpeed: number; // Blink speed in milliseconds (lower = faster)
}

/**
 * Powerup configuration map
 */
export interface PowerupConfigMap {
  [key: string]: PowerupConfig;
}

/**
 * Load powerup configurations from JSON
 */
const powerupConfigs: PowerupConfigMap = powerupConfigData.powerups as PowerupConfigMap;

/**
 * Export powerup types for easy access
 */
export const POWERUP_TYPES = powerupConfigs;

/**
 * Available powerup type names
 */
export type PowerupTypeName = keyof typeof powerupConfigs;

/**
 * Helper to get powerup config from type name
 */
export function getPowerupConfig(typeName: PowerupTypeName): PowerupConfig {
  return powerupConfigs[typeName];
}

/**
 * Get all available powerup type names
 */
export function getAllPowerupTypes(): PowerupTypeName[] {
  return Object.keys(powerupConfigs) as PowerupTypeName[];
}

/**
 * Get all unique powerup assets for loading
 */
export function getAllPowerupAssets(): Array<{ key: string; path: string }> {
  return Object.values(powerupConfigs).map(config => ({
    key: config.sprite,
    path: config.assetPath
  }));
}
